// @ts-nocheck
/**
 * useGoogleAuth - Google authentication hook
 * 
 * @deprecated Use `useSocialAuth` instead for multi-provider support (Google, Apple, Kakao, Naver)
 * This hook is maintained for backward compatibility.
 * 
 * Handles:
 * - Firebase Auth state observation
 * - Google sign-in (popup + redirect fallback)
 * - Automatic profile population from Google account
 * - Age migration for legacy profiles
 */
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  auth,
  googleProvider,
  db,
} from '../services/firebaseConfig';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { isWhitelisted, PILOT_MODE_ENABLED } from '../config/adminConfig';
import { recordAuthTimestamp, clearAuthTimestamp } from '../services/reauthService';
import { initActivityTracking, setupUnloadHandler, updateLastVisitDate } from '../services/activityTrackingService';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { trackLeadCreated } from '../services/eventTrackingService';
import { getStoredUtmParams, clearStoredUtmParams } from '../utils/utmCapture';
/**
 * Calculate age group from age
 */
const getAgeGroup = (age) => {
  if (age >= 20 && age < 30) return '20s';
  if (age >= 30 && age < 40) return '30s';
  if (age >= 40 && age < 50) return '40s';
  if (age >= 50 && age < 60) return '50s';
  if (age >= 60) return '60+';
  return null;
};

/**
 * Fetch birthday/gender from Google People API
 */
const fetchGoogleProfileExtras = async (tokenResponse) => {
  if (!tokenResponse?.idToken && !tokenResponse?.oauthAccessToken) {
    return { birthday: null, gender: null, age: null, ageGroup: null };
  }

  try {
    const accessToken = tokenResponse.oauthAccessToken || tokenResponse.idToken;
    const response = await fetch(
      'https://people.googleapis.com/v1/people/me?personFields=birthdays,genders',
      { headers: { 'Authorization': `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      console.warn('⚠️ People API failed:', response.status);
      return { birthday: null, gender: null, age: null, ageGroup: null };
    }

    const data = await response.json();
    let result = { birthday: null, gender: null, age: null, ageGroup: null };

    // Extract birthday
    if (data.birthdays?.[0]?.date?.year) {
      const bd = data.birthdays[0].date;
      result.birthday = `${bd.year}-${String(bd.month || 1).padStart(2, '0')}-${String(bd.day || 1).padStart(2, '0')}`;
      result.age = new Date().getFullYear() - bd.year;
      result.ageGroup = getAgeGroup(result.age);
    }

    // Extract gender
    if (data.genders?.[0]?.value) {
      result.gender = data.genders[0].value;
    }

    return result;
  } catch (error) {
    console.warn('⚠️ Could not fetch Google extras:', error.message);
    return { birthday: null, gender: null, age: null, ageGroup: null };
  }
};

/**
 * Custom hook for Google authentication
 * 
 * @param hydrateProfile - Function to load user profile from Firestore
 * @param resetProfileState - Function to reset profile state on logout
 * @param setIsLoggedIn - State setter for login status (from parent)
 * @param setLoginError - State setter for login errors (from parent)
 * @param setAuthChecking - State setter for auth checking status (optional)
 * @param setIsSigningIn - State setter for signing-in progress indicator (optional)
 */
export function useGoogleAuth({ 
  hydrateProfile, 
  resetProfileState,
  setIsLoggedIn,
  setLoginError,
  setAuthChecking,
  setIsSigningIn,
}) {
  const { t } = useTranslation('common');
  // Ref to track if processSignInResult is initialized
  const processSignInResultRef = useRef(null);
  
  // Ref to track if a manual sign-in is in progress
  // This prevents onAuthStateChanged from duplicating the login flow
  const isManualSignInRef = useRef(false);

  /**
   * Process sign-in result (used by both popup and redirect)
   */
  const processSignInResult = useCallback(async (result) => {
    // Clear any previous error first
    setLoginError(null);

    const user = result.user;
    const googleName = user.displayName;
    const googleEmail = user.email;
    const googlePhoto = user.photoURL;

    console.log('👤 Google sign-in:', { googleName, googleEmail });
    initActivityTracking(user.uid);
    setupUnloadHandler();
    // 🔒 Pilot Program Whitelist Check
    if (PILOT_MODE_ENABLED && !isWhitelisted(googleEmail)) {
      console.warn('⛔ Access denied: email not in pilot whitelist:', googleEmail);
      
      // Sign out the user immediately
      await auth.signOut();
      
      setLoginError(
        'Access restricted to pilot program participants. ' +
        'Please contact the admin to request access.'
      );
      return;
    }

    // Record visit date only after access is confirmed
    updateLastVisitDate(user.uid);

    // Fetch additional profile info
    const extras = await fetchGoogleProfileExtras(result._tokenResponse);

    // Phase 1 Step 4: read only from masterProfiles
    const profileDoc = doc(db, 'masterProfiles', user.uid);
    const profileSnap = await getDoc(profileDoc);
    const existingProfile = profileSnap.exists() ? profileSnap.data() : {};

    const loginCount = (existingProfile.meta?.loginCount || 0) + 1;
    // Capture before shallow-copy mutation aliases existingProfile.meta
    const previousLogin = existingProfile.meta?.lastLogin || null;

    // Phase 1 Step 4: writes go only to masterProfiles (users collection removed)

    // Update profile with Google data
    // (profileDoc and existingProfile already fetched above for login metadata)
    const profileUpdates = { ...existingProfile };
    let hasUpdates = false;

    if (!profileUpdates.identity) profileUpdates.identity = {};

    // Basic info
    if (googleName && !profileUpdates.identity.name) {
      profileUpdates.identity.name = googleName;
      hasUpdates = true;
    }
    if (googleEmail && !profileUpdates.identity.email) {
      profileUpdates.identity.email = googleEmail;
      hasUpdates = true;
    }
    if (googlePhoto && !profileUpdates.identity.photoURL) {
      profileUpdates.identity.photoURL = googlePhoto;
      hasUpdates = true;
    }

    // Need to add createdAt if it doesn't exist.
    // It will be used for automation email.
    if (!profileUpdates.meta || !profileUpdates.meta.createdAt) {
      profileUpdates.meta = profileUpdates.meta || {};
      profileUpdates.meta.createdAt = new Date();
      hasUpdates = true;
    }

    // Phase 1 Step 3: write login metadata to masterProfiles.meta (source of truth)
    if (!profileUpdates.meta) profileUpdates.meta = {};
    profileUpdates.meta.lastLogin = serverTimestamp();
    profileUpdates.meta.previousLogin = previousLogin;
    profileUpdates.meta.loginCount = loginCount;
    profileUpdates.meta.chatInitiations = existingProfile.meta?.chatInitiations || 0;
    profileUpdates.meta.cameraUploads = existingProfile.meta?.cameraUploads || 0;
    profileUpdates.meta.tokenUsage = existingProfile.meta?.tokenUsage || 0;
    hasUpdates = true;

    // Marketing attribution: capture UTM params for new signups
    const isNewUser = loginCount === 1;
    if (isNewUser) {
      const utmParams = getStoredUtmParams();
      if (utmParams) {
        profileUpdates.meta.acquisition = utmParams;
        clearStoredUtmParams();
      }
    }

    // Google extras
    if (extras.ageGroup && !profileUpdates.identity.ageGroup) {
      profileUpdates.identity.ageGroup = extras.ageGroup;
      hasUpdates = true;
    }
    if (extras.age && extras.age > 0 && !profileUpdates.identity.birthYear) {
      profileUpdates.identity.birthYear = new Date().getFullYear() - extras.age;
      hasUpdates = true;
    }
    if (extras.gender && (!profileUpdates.identity.gender || profileUpdates.identity.gender === 'unspecified')) {
      profileUpdates.identity.gender = extras.gender;
      hasUpdates = true;
    }

    // Set default language based on browser (uses canonical SUPPORTED_LANGUAGES list)
    if (!profileUpdates.identity.uiLanguage) {
      const browserLang = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
      const prefix = browserLang.split('-')[0];
      // Match full tag first (e.g. 'zh-cn' → 'zh-CN'), then prefix (e.g. 'de' → 'de')
      const match = SUPPORTED_LANGUAGES.find(
        (l) => l.toLowerCase() === browserLang
      ) ?? SUPPORTED_LANGUAGES.find(
        (l) => l.split('-')[0] === prefix
      );
      profileUpdates.identity.uiLanguage = match ?? 'en';
      hasUpdates = true;
    }

    if (hasUpdates) {
      try {
        await setDoc(profileDoc, profileUpdates, { merge: true });
        console.log('✅ Profile updated with Google data');

        // Track new signup as lead for marketing funnel
        if (isNewUser) {
          trackLeadCreated(user.uid, {
            source: profileUpdates.meta.acquisition?.utm_source || 'direct',
            medium: profileUpdates.meta.acquisition?.utm_medium,
            campaign: profileUpdates.meta.acquisition?.utm_campaign,
          });
        }
      } catch (profileError) {
        // Non-blocking: profile sync failure must not block login
        console.warn('⚠️ Failed to sync profile data:', profileError);
      }
    }

    // Use cache (forceServer: false) since we just wrote fresh data
    // This significantly speeds up login by avoiding redundant server fetch
    await hydrateProfile(user.uid, false);

    // Migrate legacy age field if present
    // Note: This reads the just-hydrated profile from the ref or refetches
    const freshProfileSnap = await getDoc(profileDoc);
    const freshProfile = freshProfileSnap.exists() ? freshProfileSnap.data() : {};
    
    if ('age' in (freshProfile?.identity || {})) {
      const age = freshProfile.identity.age;
      if (age > 0 && !freshProfile.identity.birthYear) {
        // Migrate age to birthYear/ageGroup
        const birthYear = new Date().getFullYear() - age;
        const ageGroup = getAgeGroup(age);
        await setDoc(profileDoc, {
          identity: { birthYear, ageGroup, age: deleteField() }
        }, { merge: true });
        console.log('✅ Migrated age to birthYear:', birthYear);
        await hydrateProfile(user.uid, false);
      } else {
        // Remove invalid age field
        await setDoc(profileDoc, {
          identity: { age: deleteField() }
        }, { merge: true });
        await hydrateProfile(user.uid, false);
      }
    }

    // Store returning user info for greeting
    const isReturningUser = !!previousLogin;
    sessionStorage.setItem('isReturningUser', String(isReturningUser));
    sessionStorage.setItem('loginCount', String(loginCount));

    // Record auth timestamp for re-auth checks (checkout security)
    recordAuthTimestamp();

    // Clear signing-in indicator before setting logged in
    setIsSigningIn?.(false);

    // Set logged in LAST - this triggers the greeting effect
    // At this point, profile is fully loaded and ready
    setIsLoggedIn(true);
  }, [hydrateProfile, setIsLoggedIn, setLoginError, setIsSigningIn]);

  // Update ref when callback changes
  useEffect(() => {
    processSignInResultRef.current = processSignInResult;
  }, [processSignInResult]);

  /**
   * Detect if running in an in-app browser (KakaoTalk, Instagram, etc.)
   * These browsers have storage partitioning which breaks OAuth flows
   */
  const isInAppBrowser = useCallback((): boolean => {
    const ua = navigator.userAgent || navigator.vendor || '';
    return /KAKAOTALK|Instagram|FB_IAB|FBAN|FBAV|Line\/|Twitter|NAVER|MicroMessenger/i.test(ua);
  }, []);

  /**
   * Handle Google sign-in button click
   */
  const handleGoogleSignIn = useCallback(async () => {
    try {
      console.log('🚀 Starting Google sign-in...');
      
      // Check if we're in an in-app browser FIRST
      if (isInAppBrowser()) {
        console.log('📱 In-app browser detected:', navigator.userAgent);
        const currentUrl = window.location.href;
        const message = 
          t('login.errors.inAppBrowser') + '\n' + currentUrl;
        
        setLoginError(message);
        
        try {
          await navigator.clipboard.writeText(currentUrl);
        } catch (e) {}
        
        return;
      }
      
      // Set flag to prevent onAuthStateChanged from duplicating the flow
      isManualSignInRef.current = true;
      
      // Show loading indicator during sign-in process
      setIsSigningIn?.(true);
      
      // Use native Google Sign-in on Capacitor (iOS/Android)
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Using native Google Sign-in via @capacitor-firebase/authentication');
        try {
          // Step 1: Sign in on the native layer (with retry for Credential Manager cold-start)
          let result;
          try {
            result = await FirebaseAuthentication.signInWithGoogle();
          } catch (firstAttemptError: any) {
            const msg = firstAttemptError?.message || String(firstAttemptError);
            // Android Credential Manager may throw NoCredentialException on cold start
            // before it has indexed available Google accounts. A single retry resolves this.
            if (msg.includes('No credentials available') || msg.includes('NoCredentialException')) {
              console.warn('⚠️ Credential Manager not ready, retrying in 1s...');
              await new Promise(r => setTimeout(r, 1000));
              result = await FirebaseAuthentication.signInWithGoogle();
            } else {
              throw firstAttemptError;
            }
          }
          
          if (!result.user) {
            throw new Error('No user returned from Google Sign-in');
          }
          
          console.log('✅ Native sign-in successful:', result.user.email);
          
          // Step 2: Sign in on the web layer using the id token
          // This is REQUIRED because the native plugin only signs in on the native layer,
          // but our Firestore operations use the Firebase Web SDK which needs its own auth state
          // See: https://github.com/capawesome-team/capacitor-firebase/blob/main/packages/authentication/docs/firebase-js-sdk.md
          if (!result.credential?.idToken) {
            throw new Error('No idToken returned from Google Sign-in - cannot sync with Web SDK');
          }
          
          const webCredential = GoogleAuthProvider.credential(result.credential.idToken);
          const webResult = await signInWithCredential(auth, webCredential);
          
          console.log('✅ Web SDK sign-in successful:', webResult.user.email);
          
          // Now auth.currentUser is populated and Firestore operations will work
          // Use the Web SDK result which has the correct property names (photoURL, not photoUrl)
          const enhancedResult = {
            user: webResult.user,
            _tokenResponse: {
              idToken: result.credential?.idToken || null,
              oauthAccessToken: result.credential?.accessToken || null,
            }
          };
          
          try {
            await processSignInResultRef.current?.(enhancedResult);
          } catch (processError: any) {
            console.error('❌ processSignInResult failed:', processError);
            setLoginError(t('login.errors.processFailed', { message: processError?.message || processError }));
            return;
          }
          
          return;
        } catch (nativeError: any) {
          console.error('❌ Native Google Sign-in failed:', nativeError);
          
          const errorMsg = nativeError?.message || nativeError?.error || String(nativeError);
          if (errorMsg.includes('cancel') || errorMsg.includes('Cancel') || 
              errorMsg.includes('12501') || errorMsg.includes('user_cancelled') ||
              errorMsg.includes('popup_closed') || errorMsg.includes('cancelled')) {
            setLoginError(t('login.errors.cancelled'));
          } else if (errorMsg.includes('12500') || errorMsg.includes('SIGN_IN_FAILED')) {
            setLoginError(t('login.errors.googleConfigError'));
          } else if (errorMsg.includes('10') || errorMsg.includes('DEVELOPER_ERROR')) {
            setLoginError(t('login.errors.developerError'));
          } else {
            setLoginError(t('login.errors.signInFailed', { message: errorMsg }));
          }
          return;
        } finally {
          isManualSignInRef.current = false;
          setIsSigningIn?.(false);
        }
      }
      
      // Web: Use popup
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ User signed in:', result.user.displayName);
      await processSignInResultRef.current?.(result);
      
      // Clear flag after sign-in is complete
      isManualSignInRef.current = false;
    } catch (error) {
      // Clear flags on error too
      isManualSignInRef.current = false;
      setIsSigningIn?.(false);
      console.error('❌ Google Sign-in Error:', error.code, error.message);

      // Handle user-disabled error by signing out and clearing state
      if (error.code === 'auth/user-disabled') {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('Error signing out disabled user:', signOutError);
        }
      }

      const errorMessages: Record<string, string> = {
        'auth/popup-blocked': t('login.errors.popupBlocked'),
        'auth/popup-closed-by-user': t('login.errors.cancelled'),
        'auth/cancelled-popup-request': t('login.errors.anotherPopupOpen'),
        'auth/internal-error': t('login.errors.internalError'),
        'auth/network-request-failed': t('login.errors.networkFailed'),
        'auth/user-disabled': t('login.errors.userDisabled'),
        'auth/user-not-found': t('login.errors.userNotFound'),
      };

      setLoginError(errorMessages[error.code] || t('login.errors.signInFailed', { message: error.message }));
    }
  }, [setLoginError, isInAppBrowser, setIsSigningIn, t]);

  // Set up persistence and auth state listener
  useEffect(() => {
    // Set persistence (web only - native uses indexedDBLocalPersistence via initializeAuth)
    if (!Capacitor.isNativePlatform() && typeof setPersistence === 'function') {
      setPersistence(auth, browserLocalPersistence)
        .then(() => console.log('Persistence set to local.'))
        .catch((err) => console.error('Error setting persistence:', err));
    }

    // On native platforms, check native auth state and sync with Web SDK
    // The native SDK maintains its own auth state which persists across app restarts
    // We need to sync this with the Web SDK for Firestore operations to work
    const checkNativeAuthState = async () => {
      if (!Capacitor.isNativePlatform()) return;
      
      console.log('📱 Checking native Firebase auth state...');
      try {
        const nativeUser = await FirebaseAuthentication.getCurrentUser();
        console.log('📱 Native auth user:', nativeUser.user?.email || 'none');
        
        if (nativeUser.user && !auth.currentUser) {
          console.log('📱 Native user found but Web SDK not signed in, syncing...');
          // Get fresh ID token from native SDK
          const tokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
          if (tokenResult.token) {
            // Sign in to Web SDK with the ID token
            const credential = GoogleAuthProvider.credential(tokenResult.token);
            const webResult = await signInWithCredential(auth, credential);
            console.log('✅ Synced native auth to Web SDK:', webResult.user.email);
          }
        }
      } catch (error: any) {
        console.log('📱 Native auth check:', error.message || 'No native user');
        // Not an error - just means no user is signed in on native layer
      }
    };
    checkNativeAuthState();

    // Check for redirect result (skip on native apps - causes errors)
    const handleRedirect = async () => {
      // Skip redirect handling on native Capacitor apps
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Skipping redirect check on native platform');
        return;
      }
      
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('✅ Redirect sign-in successful');
          await processSignInResultRef.current?.(result);
        }
      } catch (error) {
        console.error('❌ Redirect result error:', error.code);
        if (error.code === 'auth/internal-error') {
          setLoginError('로그인 오류: 네트워크 문제로 리디렉트 로그인이 실패했습니다.');
        } else {
          setLoginError(`리디렉트 로그인 실패: ${error.message}`);
        }
      }
    };
    handleRedirect();

    // Listen for auth state changes (handles page refresh when already logged in)
    console.log('🔐 Setting up onAuthStateChanged listener...');
    console.log('🔐 Current auth.currentUser:', auth.currentUser?.email || 'null');
    
    const unsubscribe = typeof onAuthStateChanged === 'function'
      ? onAuthStateChanged(auth, async (user) => {
          console.log('🔐 onAuthStateChanged fired:', user?.email || 'no user');
          
          // Skip if a manual sign-in is in progress - let that flow handle it
          if (isManualSignInRef.current) {
            console.log('🔄 Skipping onAuthStateChanged - manual sign-in in progress');
            return;
          }
          
          if (user) {
            console.log('✅ User is logged in:', user.email);
            // Hydrate profile FIRST, then set logged in
            // This ensures greeting effect has profile data
            await hydrateProfile(user.uid);
            setIsLoggedIn(true);
          } else {
            console.log('❌ No user is logged in.');
            setIsLoggedIn(false);
            resetProfileState();
          }
          // Auth check is complete - hide loading indicator
          console.log('🔐 Auth check complete, hiding loading indicator');
          setAuthChecking?.(false);
        })
      : undefined;

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [hydrateProfile, resetProfileState, setIsLoggedIn, setLoginError, setAuthChecking]);

  // Expose cleanup function for testing
  useEffect(() => {
    window.cleanupProfile = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.error('❌ Not logged in');
        return;
      }
      
      const profileDoc = doc(db, 'masterProfiles', user.uid);
      await setDoc(profileDoc, {
        identity: { age: deleteField() }
      }, { merge: true });
      
      console.log('✅ Removed old age field. Reloading...');
      await hydrateProfile(user.uid);
    };
  }, [hydrateProfile]);

  /**
   * Set manual sign-in flag - prevents onAuthStateChanged from running during sign-in
   * Used by other auth providers (Kakao) that share the same onAuthStateChanged listener
   */
  const setManualSignIn = useCallback((value: boolean) => {
    isManualSignInRef.current = value;
  }, []);

  return {
    handleGoogleSignIn,
    setManualSignIn,
  };
}
